import React, { useEffect, useState } from "react";
import {
  FileText,
  FileWarning,
  Loader2,
  AlertCircle,
  CheckSquare,
  FileCheck,
} from "lucide-react";
import contratoConfigService from "../../services/contratoConfigService";

// Tipos de contrato (igual que en ConfiguracionContrato)
const TIPO_CONTRATO = {
  DOCUSIGN: "DOCUSIGN",
  INTERNO: "INTERNO",
  SIN_FIRMA_DIGITAL: "SIN_FIRMA_DIGITAL",
};

export default function PreviewContrato({ eventoData, modoVista }) {
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configuracion, setConfiguracion] = useState(null);

  // --- DocuSign ---
  const [pdfData, setPdfData] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [errorPdf, setErrorPdf] = useState(null);

  // --- Plantilla Interna ---
  const [htmlPreview, setHtmlPreview] = useState(null);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const [errorHtml, setErrorHtml] = useState(null);

  // 1️⃣ Cargar configuración del evento
  useEffect(() => {
    const cargarConfig = async () => {
      if (!eventoData?.id) {
        setLoadingConfig(false);
        return;
      }
      try {
        const configResponse = await contratoConfigService.obtenerConfiguracion(
          eventoData.id
        );
        if (configResponse && configResponse.tipo_contrato) {
          setConfiguracion(configResponse);
        } else {
          setConfiguracion(null);
        }
      } catch (error) {
        console.error("Error al obtener config de contrato en preview:", error);
        setConfiguracion(null);
      } finally {
        setLoadingConfig(false);
      }
    };
    cargarConfig();
  }, [eventoData]);

  // 2️⃣ DocuSign: obtener PDF si aplica
  useEffect(() => {
    if (
      configuracion?.tipo_contrato === TIPO_CONTRATO.DOCUSIGN &&
      configuracion?.template_id &&
      eventoData?.id &&
      !pdfData
    ) {
      const obtenerPdf = async () => {
        setLoadingPdf(true);
        setErrorPdf(null);
        try {
          const response = await contratoConfigService.previewContrato(
            eventoData.id
          );
          const base64Data = response?.data?.pdfBase64 || response?.pdfBase64;
          if (base64Data) {
            setPdfData(base64Data);
          } else {
            setErrorPdf("No se pudo obtener el archivo de previsualización.");
          }
        } catch (error) {
          console.error("Error al solicitar el PDF Preview:", error);
          setErrorPdf(
            error.userMessage || "Error al cargar el documento de plantilla"
          );
        } finally {
          setLoadingPdf(false);
        }
      };
      obtenerPdf();
    }
  }, [configuracion, eventoData, pdfData]);

  // 3️⃣ Plantilla Interna: obtener preview (HTML o Word→HTML via mammoth)
  useEffect(() => {
    if (
      configuracion?.tipo_contrato === TIPO_CONTRATO.INTERNO &&
      configuracion?.plantilla_id &&
      !htmlPreview
    ) {
      const obtenerPreview = async () => {
        setLoadingHtml(true);
        setErrorHtml(null);
        try {
          const datosEjemplo = {
            invitado: {
              nombre: "Juan Pérez García",
              nombre_completo: "Juan Pérez García",
              email: "juan.perez@example.com",
              telefono: "+52 55 1234 5678",
              carrera: "Licenciatura en Administración de Empresas",
              carreraEstudios: "Licenciatura en Administración de Empresas",
              escuela: "Facultad de Contaduría y Administración",
              escuelaInstitucion: "Facultad de Contaduría y Administración",
              boletos: "4",
              cantidadBoletos: "4",
              contactoEmergencia: "María García (Madre) - +52 55 9876 5432",
              tutorNombre: "Roberto Pérez Lara",
              tutorApellidoPaterno: "Pérez",
              tutorApellidoMaterno: "Lara",
              tutor_nombre: "Roberto Pérez Lara",
            },
            graduado: {
              nombre: "Juan Pérez García",
              nombre_completo: "Juan Pérez García",
              email: "juan.perez@example.com",
              telefono: "+52 55 1234 5678",
              carrera: "Licenciatura en Administración de Empresas",
              escuela: "Facultad de Contaduría y Administración",
              boletos: "4",
              tutor: "Roberto Pérez Lara",
            },
            evento: {
              nombre: eventoData?.nombre_evento || eventoData?.nombreEvento || "Graduación de Generación 2026",
              nombreEvento: eventoData?.nombre_evento || eventoData?.nombreEvento || "Graduación de Generación 2026",
              descripcion: eventoData?.descripcionEvento || "Ceremonia oficial de graduación y entrega de diplomas.",
              descripcionEvento: eventoData?.descripcionEvento || "Ceremonia oficial de graduación y entrega de diplomas.",
              fecha: "20 de Julio de 2026",
              hora: "19:00",
              lugar: "Auditorio Cumbres",
              costo: "$1,500.00",
              costo_letra: "Mil quinientos pesos 00/100 M.N.",
            }
          };

          const response = await contratoConfigService.previsualizarPlantilla(
            configuracion.plantilla_id,
            datosEjemplo
          );
          // La respuesta del servicio viene como: { success, data: { html | docxBase64, tipo_plantilla, ... }, message }
          const payload = response?.data || response;
          const tipoPlantilla = payload?.tipo_plantilla;

          if (tipoPlantilla === "word" && payload?.docxBase64) {
            // Convertir el docx a HTML usando mammoth para mostrarlo en el iframe
            const mammoth = (await import("mammoth")).default;
            // base64 → ArrayBuffer
            const binary = atob(payload.docxBase64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            const result = await mammoth.convertToHtml({ arrayBuffer: bytes.buffer });
            // Envolver con estilos básicos para que se vea como documento
            const htmlCompleto = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: 'Times New Roman', Times, serif;
      font-size: 12pt;
      line-height: 1.6;
      color: #1a1a1a;
      margin: 0;
      padding: 32px 48px;
      background: #fff;
    }
    p { margin: 0.5em 0; }
    table { border-collapse: collapse; width: 100%; margin: 1em 0; }
    td, th { border: 1px solid #ccc; padding: 6px 10px; }
    h1, h2, h3 { color: #1a1a1a; }
    strong, b { font-weight: bold; }
  </style>
</head>
<body>${result.value}</body>
</html>`;
            setHtmlPreview(htmlCompleto);
          } else if (payload?.html) {
            setHtmlPreview(payload.html);
          } else {
            setErrorHtml("No se pudo obtener la vista previa de la plantilla.");
          }
        } catch (error) {
          console.error("Error al obtener preview de plantilla interna:", error);
          setErrorHtml(
            error.userMessage || "Error al cargar la plantilla interna"
          );
        } finally {
          setLoadingHtml(false);
        }
      };
      obtenerPreview();
    }
  }, [configuracion, htmlPreview]);

  // ── Cargando configuración ──────────────────────────────────────────────────
  if (loadingConfig) {
    return (
      <div className="bg-gray-50 h-full min-h-[400px] flex flex-col items-center justify-center rounded-lg">
        <Loader2 className="animate-spin text-casal w-10 h-10 mb-4" />
        <span className="text-gray-500 font-medium text-sm">
          Validando configuración...
        </span>
      </div>
    );
  }

  // ── Sin contrato configurado ────────────────────────────────────────────────
  if (!configuracion?.tipo_contrato) {
    return (
      <div
        className={`bg-gray-50 h-full min-h-[400px] flex flex-col items-center justify-center ${
          modoVista === "telefono" ? "p-4" : "p-8"
        } rounded-lg border-2 border-dashed border-gray-300`}
      >
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-sm w-full text-center border border-gray-100">
          <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileWarning className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Sin contrato asignado
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed">
            Este evento no tiene contrato configurado. Ve a la sección de
            configuración para vincular una plantilla o template de DocuSign.
          </p>
        </div>
      </div>
    );
  }

  // ── Sin Firma Digital ───────────────────────────────────────────────────────
  if (configuracion.tipo_contrato === TIPO_CONTRATO.SIN_FIRMA_DIGITAL) {
    return (
      <div
        className={`bg-gray-50 h-full min-h-[400px] flex flex-col items-center justify-center ${
          modoVista === "telefono" ? "p-4" : "p-8"
        } rounded-lg`}
      >
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-sm w-full text-center border border-gray-200">
          <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckSquare className="w-8 h-8 text-gray-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Solo aceptación de términos
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed">
            Este evento usa el modo <strong>Sin Firma Digital</strong>. Los
            invitados aceptarán los términos y condiciones sin firma electrónica.
          </p>
          <span className="inline-block mt-4 text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded-full font-medium">
            Gratis · Sin configuración adicional
          </span>
        </div>
      </div>
    );
  }

  // ── DocuSign ────────────────────────────────────────────────────────────────
  if (configuracion.tipo_contrato === TIPO_CONTRATO.DOCUSIGN) {
    return (
      <div className="bg-gray-100 h-full flex flex-col items-center justify-center p-2 sm:p-4 rounded-lg">
        <div className="w-full h-full max-w-4xl bg-white shadow-md rounded-lg flex flex-col overflow-hidden border border-gray-200">
          {/* Cabecera */}
          <div className="bg-gray-800 text-gray-100 py-3 px-4 flex justify-between items-center text-xs sm:text-sm shadow-md z-10">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-300" />
              <span className="font-semibold tracking-wide truncate">
                DocuSign · {eventoData?.nombre_evento}
              </span>
            </div>
            <div className="opacity-70 font-mono hidden sm:block bg-gray-900 px-2 py-1 rounded">
              ID: {configuracion.template_id?.substring(0, 12)}...
            </div>
          </div>

          {/* Contenido */}
          <div className="flex-1 bg-[#525659] flex items-center justify-center relative">
            {loadingPdf ? (
              <div className="flex flex-col items-center justify-center text-white bg-gray-800/80 p-8 rounded-xl backdrop-blur-sm">
                <Loader2 className="w-12 h-12 animate-spin mb-4 text-blue-400" />
                <p className="font-medium">Generando borrador en DocuSign...</p>
                <p className="text-xs text-gray-400 mt-2 max-w-xs text-center">
                  Solicitando la plantilla con todos los campos disponibles para
                  previsualizar.
                </p>
              </div>
            ) : errorPdf ? (
              <div className="bg-white p-6 max-w-sm text-center rounded-lg shadow border-l-4 border-red-500 text-gray-700">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2 text-gray-900">
                  Error al cargar visualización
                </h3>
                <p className="text-sm text-gray-500">{errorPdf}</p>
              </div>
            ) : pdfData ? (
              <iframe
                src={`data:application/pdf;base64,${pdfData}#toolbar=0&navpanes=0&view=FitH`}
                title="Previsualización de Contrato DocuSign"
                className="w-full h-full border-none"
              />
            ) : (
              <div className="text-white text-sm">Iniciando visor...</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Plantilla Interna ───────────────────────────────────────────────────────
  if (configuracion.tipo_contrato === TIPO_CONTRATO.INTERNO) {
    return (
      <div className="bg-gray-100 h-full flex flex-col items-center justify-center p-2 sm:p-4 rounded-lg">
        <div className="w-full h-full max-w-4xl bg-white shadow-md rounded-lg flex flex-col overflow-hidden border border-gray-200">
          {/* Cabecera */}
          <div className="bg-gray-800 text-gray-100 py-3 px-4 flex justify-between items-center text-xs sm:text-sm shadow-md z-10">
            <div className="flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-green-300" />
              <span className="font-semibold tracking-wide truncate">
                Plantilla Interna · {eventoData?.nombre_evento}
              </span>
            </div>
            <span className="hidden sm:block bg-green-900/60 text-green-200 px-2 py-1 rounded text-xs">
              {configuracion.modo_firma === "SOLO_ACEPTAR"
                ? "Solo aceptar"
                : "Firma digital"}
            </span>
          </div>

          {/* Contenido */}
          <div className="flex-1 flex items-center justify-center relative bg-[#525659]">
            {loadingHtml ? (
              <div className="flex flex-col items-center justify-center text-white bg-gray-800/80 p-8 rounded-xl backdrop-blur-sm">
                <Loader2 className="w-12 h-12 animate-spin mb-4 text-green-400" />
                <p className="font-medium">Cargando plantilla interna...</p>
                <p className="text-xs text-gray-400 mt-2 max-w-xs text-center">
                  Procesando la plantilla con datos de ejemplo.
                </p>
              </div>
            ) : errorHtml ? (
              <div className="bg-white p-6 max-w-sm text-center rounded-lg shadow border-l-4 border-red-500 text-gray-700">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2 text-gray-900">
                  Error al cargar plantilla
                </h3>
                <p className="text-sm text-gray-500">{errorHtml}</p>
              </div>
            ) : htmlPreview ? (
              <iframe
                srcDoc={htmlPreview}
                title="Previsualización de Plantilla Interna"
                className="w-full h-full border-none bg-white"
              />
            ) : (
              <div className="text-white text-sm">Iniciando visor...</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fallback (no debería llegar aquí)
  return null;
}
